import React from 'react';

import { Box, LinearProgress, makeStyles } from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { formatReservationDate } from 'helpers/reservationHelper';
import { useReservationDaySummary } from '../useReservationDaySummary';

const PLACEHOLDER = '—';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
        minWidth: 0,
        marginLeft: 8,
    },
    title: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    metrics: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 24,
    },
    metric: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    warning: {
        color: theme.palette.warning.main,
    },
    bar: {
        width: 112,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.palette.warning.light,
    },
    barFill: {
        borderRadius: 2,
    },
    barWarning: {
        backgroundColor: theme.palette.warning.main,
    },
    line: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginBottom: 8,
    },
});

export function formatSummaryDay(day: string): string {
    return formatReservationDate(day, i18n.t('reservations.daySummary.dayFormat'));
}

function formatCount(kind: 'reservation' | 'request', count: number | null): string {
    if (count === null) {
        return i18n.t(`reservations.daySummary.${kind}Many`, { count: PLACEHOLDER });
    }
    return count === 1
        ? i18n.t(`reservations.daySummary.${kind}One`)
        : i18n.t(`reservations.daySummary.${kind}Many`, { count });
}

/** With no active tables the summary shows "0 of 0" whatever is booked. */
function getShown(booked: number, total: number) {
    return total === 0 ? { booked: 0, total: 0 } : { booked, total };
}

function CapacityMetric({
    label,
    booked,
    total,
}: {
    label: (booked: string, total: string) => string;
    booked: number | null;
    total: number | null;
}) {
    const classes = useStyles();
    const isLoaded = booked !== null && total !== null;
    const shown = isLoaded ? getShown(booked!, total!) : null;
    const isOver = !!shown && shown.booked > shown.total;
    const progress = shown && shown.total > 0 ? Math.min(100, (shown.booked / shown.total) * 100) : 0;

    return (
        <div className={`${classes.metric} ${isOver ? classes.warning : ''}`}>
            <span>
                {label(
                    shown ? String(shown.booked) : PLACEHOLDER,
                    shown ? String(shown.total) : PLACEHOLDER
                )}
            </span>
            <LinearProgress
                aria-label={label(
                    String(shown?.booked ?? PLACEHOLDER),
                    String(shown?.total ?? PLACEHOLDER)
                )}
                variant="determinate"
                value={progress}
                className={classes.bar}
                classes={{
                    bar: `${classes.barFill} ${isOver ? classes.barWarning : ''}`,
                }}
            />
        </div>
    );
}

interface ReservationDaySummaryProps {
    day: string;
}

/** Capacity summary shown next to the search and date fields of a Reservations tab. */
export default function ReservationDaySummary({ day }: ReservationDaySummaryProps) {
    const classes = useStyles();
    const state = useReservationDaySummary(day);

    if (state.status === 'error') {
        return null;
    }
    const summary = state.status === 'loaded' ? state.summary : null;

    return (
        <Box className={classes.root} data-testid="reservations-day-summary">
            <div className={classes.title}>
                {i18n.t('reservations.daySummary.title', {
                    day: formatSummaryDay(day),
                    reservations: formatCount('reservation', summary?.reservations ?? null),
                    requests: formatCount('request', summary?.requests ?? null),
                })}
            </div>
            <div className={classes.metrics}>
                <CapacityMetric
                    label={(booked, total) => i18n.t('reservations.daySummary.seats', { booked, total })}
                    booked={summary?.seatsBooked ?? null}
                    total={summary?.seatsTotal ?? null}
                />
                <CapacityMetric
                    label={(booked, total) => i18n.t('reservations.daySummary.tables', { booked, total })}
                    booked={summary?.tablesBooked ?? null}
                    total={summary?.tablesTotal ?? null}
                />
            </div>
        </Box>
    );
}

/** The same values as one line under Date and Time of the reservation forms. */
export function ReservationDaySummaryLine({ day }: ReservationDaySummaryProps) {
    const classes = useStyles();
    const state = useReservationDaySummary(day || null);

    if (!day || state.status === 'error') {
        return null;
    }
    const summary = state.status === 'loaded' ? state.summary : null;
    const seats = summary ? getShown(summary.seatsBooked, summary.seatsTotal) : null;
    const tables = summary ? getShown(summary.tablesBooked, summary.tablesTotal) : null;

    return (
        <div className={classes.line} data-testid="reservations-day-summary-line">
            {i18n.t('reservations.daySummary.formLine', {
                day: formatSummaryDay(day),
                seatsBooked: seats?.booked ?? PLACEHOLDER,
                seatsTotal: seats?.total ?? PLACEHOLDER,
                tablesBooked: tables?.booked ?? PLACEHOLDER,
                tablesTotal: tables?.total ?? PLACEHOLDER,
            })}
        </div>
    );
}
